import { MantineTable } from '@dealstack/frontend/shared/components/Table/MantineTable/MantineTable';
import { getInvestorsTableColumns } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/Investors/InvestorsTable/investorsTableColumns';
import { ExportModal } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/Modals/ExportModal/ExportModal';
import { MIP_MANAGE_ROUTES } from '@dealstack/frontend/app/pages/Routing/routes';
import {
	investorDetailsPath,
	mipManagePath,
} from '@dealstack/frontend/app/pages/Routing/routes.utils';
import {
	useTranslationCommonUi,
	useTranslationMipManage,
} from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks';
import {
	GetParsedInvestorRes,
	InvestorCustomDetailColumnRes,
	InvestorsListStatsRes,
	ProgrammeStatus,
} from '@dealstack/shared/lib/generated';
import { useLocation, useNavigate } from 'react-router-dom';
import { useManageInvestorsTableController } from './useManageInvestorsTableController';
import { ConfirmDeleteDrawer } from '@dealstack/frontend/shared/components/ConfirmDeleteDrawer/ConfirmDeleteDrawer';
import { BulkManageInvestorCohortsDrawer } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/Investors/InvestorsTable/components/BulkManageInvestorCohortsDrawer/BulkManageInvestorCohortsDrawer.tsx';
import { MIPManageInvestorsV2Context } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/Investors/MIPManageInvestorsV2Context';
import { makeComplexDataTestId, TEST_ELEMENT_IDS } from '@dealstack/shared/tests/testElementIds';
import { useContext, useMemo } from 'react';
import { MantineTableDataOperations } from '@dealstack/frontend/shared/components/Table/MantineTable/useMantineTableDataOperations';
import { AsyncFilterMultiSelectProps } from '@dealstack/frontend/shared/components/Table/MantineTable/useMantineTableWithPaginationFilteringSorting';
import { QuickFilterOption } from '@dealstack/frontend/shared/components/Table/MantineTable/QuickFiltersSelect/QuickFiltersSelect.types';
import { useTableColPreferences } from '@dealstack/frontend/shared/hooks/useTableColPreferences/useTableColPreferences.hook';
import { StorageKeys } from '@dealstack/frontend/shared/helper/storageUtils';
import { Button, Flex } from '@mantine/core';
import { Icon } from '@dealstack/frontend/shared/components/fundament/Icon';
import { useRequiredParams } from '@dealstack/frontend/shared/utils/useRequiredParams.ts';
import { FEATURE_FLAG } from '@dealstack/shared/lib/common/featureFlag.ts';
import { useDealstackFeatureFlag } from '@dealstack/frontend/shared/hooks/useDealstackFeatureFlag/useDealstackFeatureFlag.hook.ts';

interface Props {
	investors: GetParsedInvestorRes[];
	customDetailColumns: InvestorCustomDetailColumnRes[];
	isLoading: boolean;
	stats?: InvestorsListStatsRes;
	totalRecord: number;
	dataOperations: MantineTableDataOperations;
	investorFilterOptions: { label: string; value: string }[];
	investorFilterAsync: AsyncFilterMultiSelectProps;
	pivFilterOptions: { label: string; value: string }[];
	pivFilterAsync: AsyncFilterMultiSelectProps;
	mipClassifierFilterOptions: { label: string; value: string }[];
}

export const InvestorsTable = ({
	isLoading,
	investors,
	customDetailColumns,
	stats,
	totalRecord,
	dataOperations,
	investorFilterOptions,
	investorFilterAsync,
	pivFilterOptions,
	pivFilterAsync,
	mipClassifierFilterOptions,
}: Props) => {
	const navigate = useNavigate();
	const location = useLocation();
	const tMipManage = useTranslationMipManage();
	const tCommonUi = useTranslationCommonUi();
	const { dealId, mipId } = useRequiredParams();

	const { value: isInvestmentVehicleDetailsEnabled } = useDealstackFeatureFlag(
		FEATURE_FLAG.INVESTMENT_VEHICLE_DETAILS
	);

	const getInvestmentVehicleLink = useMemo(() => {
		if (!isInvestmentVehicleDetailsEnabled) {
			return undefined;
		}
		return (personId: string) =>
			`${mipManagePath(MIP_MANAGE_ROUTES.INVESTMENT_VEHICLE_DETAILS, {
				dealId,
				mipId,
				personId,
			})}${location.search}`;
	}, [isInvestmentVehicleDetailsEnabled, dealId, mipId, location.search]);

	const enabledCustomDetails = useMemo(
		() =>
			customDetailColumns.map((c) => ({
				id: c.customDetailId,
				name: c.name,
				dataType: c.dataType,
			})),
		[customDetailColumns]
	);

	const {
		showExportModal,
		setShowExportModal,
		bulkActions,
		onNewJoiners,
		onDownloadAll,
		isNewJoinersDisabled,
		isDownloadAllDisabled,
		actionCallbacks,
		isLocked,
		disableSendInvestorInvitation,
		hasEditorRole,
		hasExportRole,
		hasKycAdminRole,
		setEligibilityFilter,
		removeInvestorDrawerOpened,
		setRemoveInvestorDrawerOpened,
		investorToRemove,
		setInvestorToRemove,
		removeInvestorMutation,
		showManageCohortsDrawer,
		setShowManageCohortsDrawer,
	} = useManageInvestorsTableController();

	const { selectedInvestors, refreshData } = useContext(MIPManageInvestorsV2Context);

	const { columnVisibility, handleColumnVisibilityChange } = useTableColPreferences({
		storageKey: StorageKeys.MIP_MANAGE_INVESTORS_TABLE_COLS,
		defaultColumnVisibility: {
			taxResidenceTin: false,
			countryOfBirth: false,
			citizenships: false,
			employeeIds: false,
		},
	});

	// Per-custom-detail columns are hidden by default. useTableColPreferences only reads defaults at
	// mount, so it can't see these async-loaded ids — merge their hidden defaults here, letting any saved
	// user toggle (already in columnVisibility) win.
	const effectiveColumnVisibility = useMemo(
		() => ({
			...Object.fromEntries(
				enabledCustomDetails.map((cd): [string, boolean] => [
					`customDetail:${cd.dataType}:${cd.id}`,
					false,
				])
			),
			...columnVisibility,
		}),
		[enabledCustomDetails, columnVisibility]
	);

	const navigateToInvestor = (investorId: string) => {
		navigate({
			pathname: investorDetailsPath({
				dealId,
				mipId,
				investorId,
			}),
			search: location.search,
		});
	};

	const quickFilterOptions: QuickFilterOption[] = useMemo(
		() => [
			{
				value: 'joiners',
				label: tMipManage('investors.quick_filters.joiners'),
				filterKey: 'programmeStatuses',
				filterValue: ProgrammeStatus.JOINER,
			},
			{
				value: 'holders',
				label: tMipManage('investors.quick_filters.holders'),
				filterKey: 'programmeStatuses',
				filterValue: ProgrammeStatus.HOLDER,
			},
			{
				value: 'joiners_holders',
				label: tMipManage('investors.quick_filters.joiners_and_holders'),
				filterKey: 'programmeStatuses',
				filterValue: `${ProgrammeStatus.JOINER},${ProgrammeStatus.HOLDER}`,
			},
			{
				value: 'leavers',
				label: tMipManage('investors.quick_filters.leavers'),
				filterKey: 'programmeStatuses',
				filterValue: ProgrammeStatus.LEAVER,
			},
			{
				value: 'all',
				label: tMipManage('investors.quick_filters.all'),
			},
		],
		[tMipManage]
	);

	const columns = useMemo(
		() =>
			getInvestorsTableColumns({
				tMipManage,
				tCommonUi,
				stats,
				actionCallbacks,
				isLocked,
				disableSendInvestorInvitation,
				currency: stats?.totalHoldingsCurrency,
				investorFilterOptions,
				investorFilterAsync,
				pivFilterOptions,
				pivFilterAsync,
				mipClassifierFilterOptions,
				hasEditorRole,
				hasExportRole,
				hasKycAdminRole,
				dealId,
				mipId,
				enabledCustomDetails,
				getInvestmentVehicleLink,
			}),
		[
			tMipManage,
			tCommonUi,
			stats,
			actionCallbacks,
			isLocked,
			disableSendInvestorInvitation,
			investorFilterOptions,
			investorFilterAsync,
			pivFilterOptions,
			pivFilterAsync,
			mipClassifierFilterOptions,
			hasEditorRole,
			hasExportRole,
			hasKycAdminRole,
			dealId,
			mipId,
			enabledCustomDetails,
			getInvestmentVehicleLink,
		]
	);

	return (
		<Flex w="100%" direction="column" gap="1rem">
			<Flex justify="flex-end" gap="0.5rem">
				<Button
					leftSection={<Icon variant="download-v3" size="lg" />}
					variant="default"
					onClick={onDownloadAll}
					disabled={isDownloadAllDisabled}
				>
					{tMipManage('documents_page.table.middle_bar.buttons.download_all')}
				</Button>
				<Button
					variant="default"
					leftSection={<Icon variant="add" size="lg" />}
					onClick={onNewJoiners}
					disabled={isNewJoinersDisabled}
				>
					{tMipManage('investors.actions.new_joiners')}
				</Button>
			</Flex>
			<Flex h="100%" style={{ overflow: 'hidden' }}>
				<MantineTable<GetParsedInvestorRes>
					data={investors}
					columns={columns}
					isLoading={isLoading}
					getRowId={(row) => row.id}
					enableRowSelection={true}
					enablePagination={true}
					enableColumnResizing={true}
					pinnedColumnIds={['investor']}
					manualPagination={true}
					manualSorting={true}
					rowCount={totalRecord}
					dataOperations={dataOperations}
					quickFilterOptions={quickFilterOptions}
					initialState={{ columnVisibility: effectiveColumnVisibility }}
					state={{ columnVisibility: effectiveColumnVisibility }}
					onColumnVisibilityChange={handleColumnVisibilityChange}
					renderBulkActionsMenu={true}
					onActionCanceled={() => setEligibilityFilter(null)}
					actions={bulkActions}
					mantineTableBodyRowProps={({ row }) => ({
						'data-testid': makeComplexDataTestId(
							TEST_ELEMENT_IDS.MIP_MANAGE_INVESTORS_TABLE_INVESTORS_ROW,
							row.original.person.email
						),
						style: { height: 'unset', cursor: 'pointer' },
						onClick: () => navigateToInvestor(row.original.id),
					})}
				/>
			</Flex>
			{showExportModal && (
				<ExportModal opened={showExportModal} onClose={() => setShowExportModal(false)} />
			)}
			<BulkManageInvestorCohortsDrawer
				mipId={mipId ?? ''}
				investorIds={selectedInvestors.map((i) => i.id)}
				opened={showManageCohortsDrawer}
				onClose={() => setShowManageCohortsDrawer(false)}
				onSuccess={refreshData}
			/>
			<ConfirmDeleteDrawer
				opened={removeInvestorDrawerOpened}
				onClose={() => {
					setRemoveInvestorDrawerOpened(false);
					setInvestorToRemove(null);
				}}
				title={tMipManage('investors.expanded_row.manage_investor.remove_investor_button')}
				promptText={tMipManage('investors.remove_drawer.prompt')}
				confirmDeleteText={investorToRemove?.displayName ?? ''}
				inputLabel={tMipManage('investors.remove_drawer.input_label')}
				inputPlaceholder={tMipManage('investors.remove_drawer.input_placeholder')}
				wordMismatchErrorMessage={tMipManage('investors.remove_drawer.mismatch_error')}
				deleteBtnText={tMipManage('investors.remove_drawer.delete_button')}
				cancelBtnText={tMipManage('investors.remove_drawer.cancel_button')}
				handleDelete={() => {
					if (investorToRemove) {
						removeInvestorMutation.mutate(investorToRemove.id);
					}
				}}
				deleteSuccess={removeInvestorMutation.isSuccess}
				deleteLoading={removeInvestorMutation.isPending}
				enableDeleteButtonOnlyOnConfirmDelete
			/>
		</Flex>
	);
};
